import { Address, Listing } from 'graphql/types'
import { TProps as TBreadcrumbProps } from 'components/Breadcrumb'
import logger from './logger'

export type TGetSearchListingBreadcrumbList = {
  listingId?: Listing['id']
  address: Address
}

type TListingSearchType = 'search' | 'listing'

type TSendListingSearchEventParams = {
  type: TListingSearchType
  props: {
    url: string
    label: string
  }
}

const sendListingSearchEvent = ({
  type,
  props,
}: TSendListingSearchEventParams) => {
  const eventName =
    type === 'search'
      ? 'listings-search-breadcrumb'
      : 'listing-detail-breadcrumb'

  logger.action(eventName, props)
}

export const getListingSearchBreadcrumbList = ({
  listingId,
  address: {
    state,
    stateSlug,
    city,
    citySlug,
    neighborhood,
    neighborhoodSlug,
    street,
    streetSlug,
    condominium,
  },
}: TGetSearchListingBreadcrumbList): TBreadcrumbProps['list'] => {
  if (!city || !citySlug || !state || !stateSlug) return []

  const type = listingId ? 'listing' : 'search'

  const optionalItems = ['neighborhood', 'street', 'condominium', 'listingId']

  const getOptionalItems = () =>
    optionalItems.reduce<TBreadcrumbProps['list']>((prev, curr) => {
      switch (curr) {
        case 'neighborhood':
          if (!neighborhood || !neighborhoodSlug) return prev

          return [
            ...prev,
            {
              to: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}`,
              label: neighborhood,
              active: !street && !condominium,
              onClick: () =>
                sendListingSearchEvent({
                  type,
                  props: {
                    url: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}`,
                    label: neighborhood,
                  },
                }),
            },
          ]
        case 'street':
          if (!neighborhood || !neighborhoodSlug || !street || !streetSlug)
            return prev

          return [
            ...prev,
            {
              to: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}/${streetSlug}`,
              label: street,
              active: !listingId,
              onClick: () =>
                sendListingSearchEvent({
                  type,
                  props: {
                    url: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}/${streetSlug}`,
                    label: street,
                  },
                }),
            },
          ]
        case 'condominium':
          if (!neighborhood || !neighborhoodSlug || !condominium) return prev

          return [
            ...prev,
            {
              to: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}/${
                condominium?.nameSlug as string
              }`,
              label: condominium?.name as string,
              active: true,
              onClick: () =>
                sendListingSearchEvent({
                  type,
                  props: {
                    url: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}/${
                      condominium?.nameSlug as string
                    }`,
                    label: condominium?.name as string,
                  },
                }),
            },
          ]
        case 'listingId':
          if (!neighborhoodSlug || !streetSlug || !listingId) return prev

          return [
            ...prev,
            {
              to: `/imoveis/${stateSlug}/${citySlug}/${neighborhoodSlug}/${streetSlug}/id-${listingId}`,
              label: `ID ${listingId}`,
              active: true,
            },
          ]
        default:
          return prev
      }
    }, [])

  return [
    {
      to: '/',
      label: 'Página inicial',
      onClick: () =>
        sendListingSearchEvent({
          type,
          props: {
            url: '/',
            label: 'Página inicial',
          },
        }),
    },
    {
      to: `/imoveis/${stateSlug}/${citySlug}`,
      label: `${city}, ${state}`,
      active: !neighborhood,
      onClick: () =>
        sendListingSearchEvent({
          type,
          props: {
            url: `/imoveis/${stateSlug}/${citySlug}`,
            label: `${city}, ${state}`,
          },
        }),
    },
    ...getOptionalItems(),
  ]
}
